import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import {
  handleIntegrations,
  IntegrationsService,
} from './app/shared/util/integrations';
import { AppComponent } from './app/app.component';
import { ToastrModule } from 'ngx-toastr';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { appRoutingProviders } from './app/app-routing.module';
import { bootstrapApplication } from '@angular/platform-browser';
import { DatePipe, DOCUMENT } from '@angular/common';
import { ErrorInterceptor } from './app/shared/api/error.interceptor';
import { AuthInterceptor } from './app/user/auth-interceptor';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { Router, RouteReuseStrategy } from '@angular/router';
import { CustomRouteReuseStrategy } from './app/custom-route-reuse.strategy';
import { LoadingInterceptor } from './app/shared/api/loading.interceptor';
import { MAT_DIALOG_SCROLL_STRATEGY } from '@angular/material/dialog';
import { PageScrollLockStrategy } from './app/shared/services/page-scroll-lock';

/**
 * Analytics is configured during bootstrap but never awaited.
 *
 * The braces are load-bearing: this factory discards whatever init() returns,
 * so the initializer resolves immediately even if init() ever grows an
 * `await`. Angular delays the first render for as long as an initializer's
 * promise is pending, and third-party tags are not worth a visitor's paint.
 * See IntegrationsService.init(), which defers Hotjar and Sentry's replay
 * recorder to the first idle period. gtag is not among them and is not this
 * service's business at all: the inline gate in index.html installs its queue
 * during HTML parse - long before this bundle finishes downloading - and
 * fetches the tag itself once the browser goes idle.
 */
function initIntegrations(svc: IntegrationsService) {
  return () => {
    svc.init();
  };
}

if (environment.production) {
  enableProdMode();
  handleIntegrations();
}
//@ts-ignore
bootstrapApplication(AppComponent, {
  providers: [
    IntegrationsService,
    appRoutingProviders,
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(ToastrModule.forRoot()),
    DatePipe,
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initIntegrations,
      deps: [IntegrationsService],
      multi: true,
    },
    {
      // Every dialog freezes the page behind it with this instead of the CDK
      // default, which shifts the page (see PageScrollLockStrategy).
      provide: MAT_DIALOG_SCROLL_STRATEGY,
      useFactory: (document: Document) => () =>
        new PageScrollLockStrategy(document),
      deps: [DOCUMENT],
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
  ],
}).catch((err) => console.error(err));
